/**
 * LiveCommitmentReview
 *
 * During a live meeting the organiser can toggle "Commitment Review" on/off.
 * Each member independently decides whether to share their open commitments.
 * Commitments originate from the PostSessionReflection (concreteNextAction).
 *
 * Firestore paths used:
 *   meetingSettings/{entityId}.commitmentReviewEnabled   — admin toggle (boolean)
 *   commitmentSharing/{entityId}_{meetingId}_{userId}    — per-member opt-in
 */

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  CheckIcon,
  CloseIcon,
  GoalIcon,
  HourglassIcon,
  Loader,
} from '../../components/icons';
import {
  getOpenCommitmentsForEntity,
  updateCommitmentStatus,
} from './structuredGrowthService';
import type { Commitment, CommitmentStatus } from './types';

interface LiveCommitmentReviewProps {
  entityId: string;
  meetingId: string;
  currentUserId: string;
  isAdmin: boolean;
  memberNames?: string[];
  memberAvatars?: (string | null)[];
  activeUserId?: string;
}

function formatDeadline(ts: Commitment['deadline']): string {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date();
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

const ToggleSwitch: React.FC<{ on: boolean; onChange: (v: boolean) => void; size?: 'sm' | 'xs' }> = ({ on, onChange, size = 'sm' }) => {
  const w = size === 'xs' ? 'w-7' : 'w-8';
  const h = size === 'xs' ? 'h-3.5' : 'h-4';
  const dot = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  const translate = size === 'xs' ? 'translate-x-3.5' : 'translate-x-4';
  return (
    <button type="button" onClick={() => onChange(!on)}
      className={`${w} ${h} rounded-full relative transition-colors duration-200 ${on ? 'bg-emerald-500' : 'bg-gray-600'}`}>
      <span className={`absolute top-0.5 left-0.5 ${dot} rounded-full bg-white transition-transform duration-200 ${on ? translate : 'translate-x-0'}`} />
    </button>
  );
};

const LiveCommitmentReview: React.FC<LiveCommitmentReviewProps> = ({
  entityId, meetingId, currentUserId, isAdmin, memberNames = [], memberAvatars = [], activeUserId,
}) => {
  // Accordion: welk lid is uitgeklapt (null = allemaal ingeklapt)
  // Auto-expand wanneer het actieve lid (aan de beurt) verandert
  const [expandedUid, setExpandedUid] = useState<string | null>(activeUserId ?? null);

  useEffect(() => {
    if (activeUserId) setExpandedUid(activeUserId);
  }, [activeUserId]);
  // ── Admin toggle: is the commitment review enabled? ────────────────────
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [reviewEnabledLoading, setReviewEnabledLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, 'meetingSettings', entityId), (snap) => {
      const data = snap.data();
      setReviewEnabled(!!data?.commitmentReviewEnabled);
      setReviewEnabledLoading(false);
    });
    return () => unsub();
  }, [entityId]);

  const toggleReviewEnabled = useCallback(async (enabled: boolean) => {
    setReviewEnabled(enabled);
    await setDoc(doc(db, 'meetingSettings', entityId), { commitmentReviewEnabled: enabled }, { merge: true }).catch(() => {});
  }, [entityId]);

  // ── Per-member sharing opt-in ──────────────────────────────────────────
  const sharingDocId = `${entityId}_${meetingId}_${currentUserId}`;
  const [sharingOptIn, setSharingOptIn] = useState(false);

  useEffect(() => {
    if (!sharingDocId) return;
    const unsub = onSnapshot(doc(db, 'commitmentSharing', sharingDocId), (snap) => {
      setSharingOptIn(!!snap.data()?.shared);
    });
    return () => unsub();
  }, [sharingDocId]);

  const toggleSharing = useCallback(async (shared: boolean) => {
    setSharingOptIn(shared);
    await setDoc(doc(db, 'commitmentSharing', sharingDocId), {
      entityId, meetingId, userId: currentUserId, shared, updatedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});
  }, [sharingDocId, entityId, meetingId, currentUserId]);

  // ── Commitment data ────────────────────────────────────────────────────
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharedUserIds, setSharedUserIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!reviewEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await getOpenCommitmentsForEntity(entityId);
      setCommitments(data);
    } finally {
      setLoading(false);
    }
  }, [entityId, reviewEnabled]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  // Listen to all sharing opt-ins for this meeting to know whose commitments to display
  useEffect(() => {
    if (!entityId || !meetingId) return;
    // We listen to the collection using a prefix pattern via individual snapshots
    // Alternative: listen to the meetingSettings doc for a sharedUsers array
    // For now, poll the commitments' userIds against their sharing docs
    const prefix = `${entityId}_${meetingId}_`;
    const userIds = [...new Set(commitments.map((c) => c.userId))];
    if (userIds.length === 0) return;

    const unsubs = userIds.map((uid) =>
      onSnapshot(doc(db, 'commitmentSharing', `${prefix}${uid}`), (snap) => {
        setSharedUserIds((prev) => {
          const next = new Set(prev);
          if (snap.data()?.shared) next.add(uid);
          else next.delete(uid);
          return next;
        });
      }),
    );
    return () => unsubs.forEach((u) => u());
  }, [entityId, meetingId, commitments]);

  const handleStatus = useCallback(async (id: string, status: CommitmentStatus) => {
    setCommitments((prev) => prev.filter((c) => c.id !== id));
    try { await updateCommitmentStatus(id, status); } catch { load(); }
  }, [load]);

  // Only show commitments from users who opted in
  const visibleCommitments = useMemo(
    () => commitments.filter((c) => sharedUserIds.has(c.userId)),
    [commitments, sharedUserIds],
  );

  const grouped = useMemo(() => {
    return visibleCommitments.reduce<Record<string, Commitment[]>>((acc, c) => {
      (acc[c.userId] ??= []).push(c);
      return acc;
    }, {});
  }, [visibleCommitments]);

  const userIds = Object.keys(grouped);

  // My own open commitments (for the sharing toggle preview)
  const myCommitments = useMemo(
    () => commitments.filter((c) => c.userId === currentUserId),
    [commitments, currentUserId],
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      {/* Header with admin toggle */}
      <div className="flex items-center gap-1.5 px-1">
        <GoalIcon size={13} weight="regular" className="text-amber-400" />
        <span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">Commitment Review</span>
        {isAdmin && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[8px] text-gray-500">{reviewEnabled ? 'Aan' : 'Uit'}</span>
            {reviewEnabledLoading ? (
              <Loader size={10} strokeWidth={1.5} className="animate-spin text-gray-500" />
            ) : (
              <ToggleSwitch on={reviewEnabled} onChange={toggleReviewEnabled} size="xs" />
            )}
          </div>
        )}
      </div>

      {/* Not enabled — admin sees hint, members see nothing */}
      {!reviewEnabled && (
        <div className="text-center py-2">
          <p className="text-[9px] text-gray-500">
            {isAdmin ? 'Zet de toggle aan om commitments te reviewen tijdens de meeting.' : 'Commitment review is niet actief.'}
          </p>
        </div>
      )}

      {reviewEnabled && (
        <>
          {/* Per-member sharing opt-in */}
          <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-700/40 rounded-lg border border-gray-700">
            <ToggleSwitch on={sharingOptIn} onChange={toggleSharing} size="xs" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-200 font-medium leading-tight">
                Mijn commitments delen
              </p>
              <p className="text-[8px] text-gray-500 leading-tight">
                {sharingOptIn
                  ? `${myCommitments.length} commitment${myCommitments.length !== 1 ? 's' : ''} zichtbaar voor de groep`
                  : 'Andere leden kunnen jouw commitments niet zien'}
              </p>
            </div>
            {sharingOptIn && myCommitments.length > 0 && (
              <span className="text-[9px] text-emerald-400 font-semibold shrink-0">{myCommitments.length}</span>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-3">
              <Loader size={14} strokeWidth={1.5} className="animate-spin text-gray-400" />
            </div>
          )}

          {/* Empty state */}
          {!loading && userIds.length === 0 && (
            <div className="text-center py-3">
              <p className="text-[9px] text-gray-500">
                {commitments.length === 0
                  ? 'Geen openstaande commitments gevonden uit eerdere reflecties.'
                  : 'Nog geen leden hebben hun commitments gedeeld.'}
              </p>
            </div>
          )}

          {/* Commitment cards — accordion per lid */}
          {!loading && userIds.length > 0 && (
            <>
              <div className="flex items-center justify-between px-1">
                <span className="text-[9px] text-gray-500">{visibleCommitments.length} open · {userIds.length} {userIds.length === 1 ? 'lid' : 'leden'}</span>
                {expandedUid && (
                  <button type="button" onClick={() => setExpandedUid(null)} className="text-[8px] text-gray-500 hover:text-gray-300 transition-colors">Alles inklappen</button>
                )}
              </div>
              <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {userIds.map((uid) => {
                  const userCommitments = grouped[uid];
                  const first = userCommitments[0];
                  const isActive = uid === activeUserId;
                  const isMe = uid === currentUserId;
                  const isOpen = expandedUid === uid;
                  const initials = (first.userName || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();

                  return (
                    <div key={uid} className={`rounded-lg border transition-all ${isActive ? 'border-amber-500/50 bg-amber-900/20' : isOpen ? 'border-gray-600 bg-gray-800/70' : 'border-gray-700/60 bg-gray-800/30'}`}>
                      {/* Accordion header — altijd zichtbaar */}
                      <button
                        type="button"
                        onClick={() => setExpandedUid(isOpen ? null : uid)}
                        className="w-full flex items-center gap-2 px-2.5 py-2 hover:bg-white/3 transition-colors rounded-lg"
                      >
                        <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center shrink-0 overflow-hidden">
                          <span className="text-[8px] font-bold text-gray-300">{initials}</span>
                        </div>
                        <span className="text-[11px] font-semibold text-gray-200 truncate flex-1 text-left">
                          {first.userName}{isMe ? ' (jij)' : ''}
                        </span>
                        {isActive && <span className="text-[8px] bg-amber-500/30 text-amber-300 px-1.5 py-0.5 rounded font-medium shrink-0">Aan beurt</span>}
                        <span className="text-[9px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-semibold shrink-0">{userCommitments.length}</span>
                        <svg className={`w-3 h-3 text-gray-500 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Accordion body — commitments */}
                      {isOpen && (
                        <div className="px-2.5 pb-2.5 space-y-1 border-t border-white/5 pt-2">
                          {userCommitments.map((c) => (
                            <div key={c.id} className="flex items-start gap-2 bg-gray-700/50 rounded-md px-2 py-1.5 group">
                              <GoalIcon size={11} weight="regular" className="text-amber-400 shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-gray-200 leading-snug">{c.commitmentText}</p>
                                {c.deadline && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <HourglassIcon size={9} weight="regular" className="text-gray-500" />
                                    <span className="text-[8px] text-gray-500">{formatDeadline(c.deadline)}</span>
                                  </div>
                                )}
                              </div>
                              {isAdmin && (
                                <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => handleStatus(c.id, 'fulfilled')}
                                    className="w-5 h-5 rounded flex items-center justify-center bg-emerald-600/30 text-emerald-400 hover:bg-emerald-600/60 transition-colors"
                                    title="Nagekomen">
                                    <CheckIcon size={10} weight="bold" />
                                  </button>
                                  <button onClick={() => handleStatus(c.id, 'missed')}
                                    className="w-5 h-5 rounded flex items-center justify-center bg-red-600/30 text-red-400 hover:bg-red-600/60 transition-colors"
                                    title="Niet nagekomen">
                                    <CloseIcon size={10} weight="bold" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default memo(LiveCommitmentReview);
